import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { GeneticVariantViewModel } from '@/features/patient-record/types';

import { INTERPRETATION_SHORT, INTERPRETATION_TONE } from './constants';
import { SectionHeading } from './SummaryItem';

const VISIBLE_LIMIT = 10;

interface VariantsTooltipProps {
  readonly variants: readonly GeneticVariantViewModel[];
  readonly totalCount: number;
}

export function VariantsTooltip({
  variants,
  totalCount,
}: VariantsTooltipProps): ReactElement {
  const { t } = useTranslation();
  const visible = variants.slice(0, VISIBLE_LIMIT);
  const overflow = totalCount - visible.length;

  return (
    <div className="space-y-1.5 min-w-[260px]">
      <SectionHeading
        text={t('patient.geneticSummary.tooltips.variantsTitle', {
          count: totalCount,
        })}
      />
      <ul className="space-y-1">
        {visible.map((variant) => (
          <li key={variant.id} className="flex items-center gap-2 text-xs">
            {variant.interpretation !== null && (
              <span
                className={cn(
                  'rounded px-1.5 py-0 text-[10px] font-semibold whitespace-nowrap',
                  INTERPRETATION_TONE[variant.interpretation],
                )}
              >
                {INTERPRETATION_SHORT[variant.interpretation]}
              </span>
            )}
            <span className="font-medium text-foreground">{variant.gene}</span>
            <span className="text-muted-foreground truncate">{variant.variant}</span>
            {variant.zygosity !== null && (
              <span className="text-muted-foreground/80 text-[10px] ml-auto shrink-0">
                {variant.zygosity}
              </span>
            )}
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {t('patient.geneticSummary.tooltips.andMore', { count: overflow })}
        </p>
      )}
    </div>
  );
}
